import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { authFetch } from '../contexts/AuthContext';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import {
  MessageSquare,
  Briefcase,
  Calculator,
  BarChart3,
  Calendar,
  ArrowRight,
  Loader2,
  FileText,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function SavedResultsPage() {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  useEffect(() => {
    fetchSavedResults();
  }, []);

  const fetchSavedResults = async () => {
    try {
      const response = await authFetch(`${API_URL}/api/saved-results`);
      if (response.ok) {
        const results = await response.json();
        setData(results);
      }
    } catch (error) {
      console.error('Error fetching saved results:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('es-MX', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN',
      minimumFractionDigits: 0,
    }).format(amount);
  };

  const getTotalCount = () => {
    if (!data) return 0;
    return (
      (data.chat_sessions?.length || 0) +
      (data.job_evaluations?.length || 0) +
      (data.severance_calculations?.length || 0) +
      (data.salary_benchmarks?.length || 0)
    );
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[400px]">
          <Loader2 className="w-8 h-8 animate-spin text-[#2563EB]" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6" data-testid="saved-results-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-[#0F172A] font-['Manrope']">
              Resultados Guardados
            </h1>
            <p className="mt-2 text-muted-foreground">
              {getTotalCount()} resultados guardados
            </p>
          </div>
        </div>

        <Tabs defaultValue="all" className="w-full">
          <TabsList className="grid w-full grid-cols-5 max-w-2xl">
            <TabsTrigger value="all" data-testid="tab-all">Todos</TabsTrigger>
            <TabsTrigger value="chat" data-testid="tab-chat">Chat</TabsTrigger>
            <TabsTrigger value="offers" data-testid="tab-offers">Ofertas</TabsTrigger>
            <TabsTrigger value="severance" data-testid="tab-severance">Liquidación</TabsTrigger>
            <TabsTrigger value="benchmark" data-testid="tab-benchmark">Benchmark</TabsTrigger>
          </TabsList>

          {/* All Tab */}
          <TabsContent value="all" className="mt-6">
            <div className="space-y-6">
              {/* Chat Sessions */}
              {data?.chat_sessions?.length > 0 && (
                <section>
                  <h3 className="text-lg font-semibold text-[#0F172A] font-['Manrope'] mb-3 flex items-center gap-2">
                    <MessageSquare className="w-5 h-5 text-blue-500" />
                    Conversaciones
                  </h3>
                  <div className="grid gap-3">
                    {data.chat_sessions.slice(0, 3).map((session) => (
                      <Link
                        key={session.session_id}
                        to={`/advisor?session=${session.session_id}`}
                        className="block"
                      >
                        <Card className="border hover:shadow-md hover:border-[#2563EB]/30 transition-all">
                          <CardContent className="p-4 flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                                <MessageSquare className="w-5 h-5 text-blue-600" />
                              </div>
                              <div>
                                <p className="font-medium text-[#0F172A]">{session.title}</p>
                                <p className="text-sm text-muted-foreground">
                                  {formatDate(session.updated_at)}
                                </p>
                              </div>
                            </div>
                            <ArrowRight className="w-5 h-5 text-muted-foreground" />
                          </CardContent>
                        </Card>
                      </Link>
                    ))}
                  </div>
                </section>
              )}

              {/* Job Evaluations */}
              {data?.job_evaluations?.length > 0 && (
                <section>
                  <h3 className="text-lg font-semibold text-[#0F172A] font-['Manrope'] mb-3 flex items-center gap-2">
                    <Briefcase className="w-5 h-5 text-emerald-500" />
                    Evaluaciones de Ofertas
                  </h3>
                  <div className="grid gap-3">
                    {data.job_evaluations.slice(0, 3).map((eval_) => (
                      <Card key={eval_.evaluation_id} className="border">
                        <CardContent className="p-4">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                                <Briefcase className="w-5 h-5 text-emerald-600" />
                              </div>
                              <div>
                                <p className="font-medium text-[#0F172A]">{eval_.input_data?.role}</p>
                                <p className="text-sm text-muted-foreground">
                                  {eval_.input_data?.city} • {formatDate(eval_.created_at)}
                                </p>
                              </div>
                            </div>
                            <div className="text-right">
                              <Badge variant={eval_.offer_score >= 65 ? 'default' : 'secondary'}>
                                Score: {eval_.offer_score}
                              </Badge>
                              <p className="text-sm font-medium text-[#2563EB] mt-1">
                                {formatCurrency(eval_.total_compensation)}/año
                              </p>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </section>
              )}

              {/* Severance Calculations */}
              {data?.severance_calculations?.length > 0 && (
                <section>
                  <h3 className="text-lg font-semibold text-[#0F172A] font-['Manrope'] mb-3 flex items-center gap-2">
                    <Calculator className="w-5 h-5 text-amber-500" />
                    Cálculos de Liquidación
                  </h3>
                  <div className="grid gap-3">
                    {data.severance_calculations.slice(0, 3).map((calc) => (
                      <Card key={calc.calculation_id} className="border">
                        <CardContent className="p-4">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                                <Calculator className="w-5 h-5 text-amber-600" />
                              </div>
                              <div>
                                <p className="font-medium text-[#0F172A]">
                                  {formatCurrency(calc.input_data?.salary)}/mes
                                </p>
                                <p className="text-sm text-muted-foreground">
                                  {formatDate(calc.created_at)}
                                </p>
                              </div>
                            </div>
                            <div className="text-right">
                              <p className="text-sm text-muted-foreground">Total estimado</p>
                              <p className="font-bold text-[#0F172A]">
                                {formatCurrency(calc.estimated_severance + calc.estimated_settlement)}
                              </p>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </section>
              )}

              {/* Salary Benchmarks */}
              {data?.salary_benchmarks?.length > 0 && (
                <section>
                  <h3 className="text-lg font-semibold text-[#0F172A] font-['Manrope'] mb-3 flex items-center gap-2">
                    <BarChart3 className="w-5 h-5 text-purple-500" />
                    Benchmarks Salariales
                  </h3>
                  <div className="grid gap-3">
                    {data.salary_benchmarks.slice(0, 3).map((bench) => (
                      <Card key={bench.benchmark_id} className="border">
                        <CardContent className="p-4">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
                                <BarChart3 className="w-5 h-5 text-purple-600" />
                              </div>
                              <div>
                                <p className="font-medium text-[#0F172A]">{bench.input_data?.role}</p>
                                <p className="text-sm text-muted-foreground">
                                  {bench.input_data?.industry} • {formatDate(bench.created_at)}
                                </p>
                              </div>
                            </div>
                            <div className="text-right">
                              <Badge variant={bench.percentile >= 50 ? 'default' : 'secondary'}>
                                Percentil {bench.percentile}
                              </Badge>
                              <p className="text-sm text-muted-foreground mt-1">
                                {bench.status}
                              </p>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </section>
              )}

              {/* Empty State */}
              {getTotalCount() === 0 && (
                <Card className="border">
                  <CardContent className="p-8 text-center">
                    <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mx-auto mb-4">
                      <FileText className="w-8 h-8 text-muted-foreground" />
                    </div>
                    <h3 className="font-semibold text-[#0F172A] font-['Manrope']">
                      No hay resultados guardados
                    </h3>
                    <p className="text-sm text-muted-foreground mt-2 mb-4">
                      Comienza a usar las herramientas para guardar tus análisis.
                    </p>
                    <Link to="/dashboard">
                      <Button className="bg-[#2563EB] hover:bg-[#2563EB]/90">
                        Ir al Dashboard
                      </Button>
                    </Link>
                  </CardContent>
                </Card>
              )}
            </div>
          </TabsContent>

          {/* Individual Tabs */}
          <TabsContent value="chat" className="mt-6">
            <div className="grid gap-3">
              {data?.chat_sessions?.length > 0 ? (
                data.chat_sessions.map((session) => (
                  <Link
                    key={session.session_id}
                    to={`/advisor?session=${session.session_id}`}
                    className="block"
                  >
                    <Card className="border hover:shadow-md hover:border-[#2563EB]/30 transition-all">
                      <CardContent className="p-4 flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                            <MessageSquare className="w-5 h-5 text-blue-600" />
                          </div>
                          <div>
                            <p className="font-medium text-[#0F172A]">{session.title}</p>
                            <p className="text-sm text-muted-foreground">
                              {formatDate(session.updated_at)}
                            </p>
                          </div>
                        </div>
                        <ArrowRight className="w-5 h-5 text-muted-foreground" />
                      </CardContent>
                    </Card>
                  </Link>
                ))
              ) : (
                <p className="text-center text-muted-foreground py-8">
                  No hay conversaciones guardadas
                </p>
              )}
            </div>
          </TabsContent>

          <TabsContent value="offers" className="mt-6">
            <div className="grid gap-3">
              {data?.job_evaluations?.length > 0 ? (
                data.job_evaluations.map((eval_) => (
                  <Card key={eval_.evaluation_id} className="border">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                            <Briefcase className="w-5 h-5 text-emerald-600" />
                          </div>
                          <div>
                            <p className="font-medium text-[#0F172A]">{eval_.input_data?.role}</p>
                            <p className="text-sm text-muted-foreground">
                              {eval_.input_data?.city} • {formatDate(eval_.created_at)}
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <Badge variant={eval_.offer_score >= 65 ? 'default' : 'secondary'}>
                            Score: {eval_.offer_score}
                          </Badge>
                          <p className="text-sm font-medium text-[#2563EB] mt-1">
                            {formatCurrency(eval_.total_compensation)}/año
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))
              ) : (
                <p className="text-center text-muted-foreground py-8">
                  No hay evaluaciones de ofertas
                </p>
              )}
            </div>
          </TabsContent>

          <TabsContent value="severance" className="mt-6">
            <div className="grid gap-3">
              {data?.severance_calculations?.length > 0 ? (
                data.severance_calculations.map((calc) => (
                  <Card key={calc.calculation_id} className="border">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                            <Calculator className="w-5 h-5 text-amber-600" />
                          </div>
                          <div>
                            <p className="font-medium text-[#0F172A]">
                              {formatCurrency(calc.input_data?.salary)}/mes
                            </p>
                            <p className="text-sm text-muted-foreground">
                              {formatDate(calc.created_at)}
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-sm text-muted-foreground">Total estimado</p>
                          <p className="font-bold text-[#0F172A]">
                            {formatCurrency(calc.estimated_severance + calc.estimated_settlement)}
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))
              ) : (
                <p className="text-center text-muted-foreground py-8">
                  No hay cálculos de liquidación
                </p>
              )}
            </div>
          </TabsContent>

          <TabsContent value="benchmark" className="mt-6">
            <div className="grid gap-3">
              {data?.salary_benchmarks?.length > 0 ? (
                data.salary_benchmarks.map((bench) => (
                  <Card key={bench.benchmark_id} className="border">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
                            <BarChart3 className="w-5 h-5 text-purple-600" />
                          </div>
                          <div>
                            <p className="font-medium text-[#0F172A]">{bench.input_data?.role}</p>
                            <p className="text-sm text-muted-foreground">
                              {bench.input_data?.industry} • {formatDate(bench.created_at)}
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <Badge variant={bench.percentile >= 50 ? 'default' : 'secondary'}>
                            Percentil {bench.percentile}
                          </Badge>
                          <p className="text-sm text-muted-foreground mt-1">
                            {bench.status}
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))
              ) : (
                <p className="text-center text-muted-foreground py-8">
                  No hay benchmarks salariales
                </p>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
}
